import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { UserService } from './api/user';
import { AuthService } from './auth/auth';
import { CurrencyTotals } from './deal-payments';

/**
 * Валюта сводок кабинета — по стране своей компании (указана при регистрации):
 * Казахстан — ₸, еврозона и остальная Европа — €, Россия — ₽, Китай — ¥, остальные — $.
 * Сводки (главная, контрагенты) считаются в этой валюте; счета в других валютах
 * не пересчитываются (курса на дату счёта пока нет — см. ТЗ) и показываются отдельной строкой «+ 3 000 USD».
 */
const BY_COUNTRY: Record<string, string> = { KZ: 'KZT', RU: 'RUB', CN: 'CNY', US: 'USD' };

/** Еврозона и страны, где в ходу евро. */
const EURO = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
  'AD', 'MC', 'SM', 'VA', 'ME', 'XK',
];

/** Остальная Европа: своей валюты среди валют счёта нет, торгуют обычно в евро. */
const EUROPE_OTHER = ['PL', 'CZ', 'HU', 'RO', 'SE', 'DK', 'NO', 'CH', 'GB', 'IS', 'LI', 'RS', 'BA', 'MK', 'AL', 'MD', 'UA'];

export function currencyByCountry(code?: string | null): string {
  const c = (code || '').toUpperCase();
  if (BY_COUNTRY[c]) return BY_COUNTRY[c];
  if (EURO.includes(c) || EUROPE_OTHER.includes(c)) return 'EUR';
  return c ? 'USD' : 'KZT';
}

@Injectable({ providedIn: 'root' })
export class HomeCurrencyService {
  /** Загруженная валюта — на сессию (токен): после выхода, входа другим пользователем или «Войти как» загружается заново. */
  private cache: { token: string | null; currency$: Observable<string> } | null = null;

  constructor(private userService: UserService, private authService: AuthService) {}

  /** Валюта сводок текущей компании. */
  get currency$(): Observable<string> {
    const token = this.authService.getToken();
    if (!this.cache || this.cache.token !== token) {
      this.cache = {
        token,
        currency$: this.userService.getCurrentUser().pipe(
          map(u => currencyByCountry(u?.country?.code)),
          catchError(() => of('KZT')),
          shareReplay(1)
        ),
      };
    }
    return this.cache.currency$;
  }
}

type Field = 'invoiced' | 'paid' | 'pending' | 'debt' | 'overpaid';

const LOCALES: Record<string, string> = { ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' };

/** Сумма с валютой: «1 130 300 ₸», «2 500 EUR». */
export function fmtMoney(v: number, currency: string, lang = 'ru'): string {
  const n = new Intl.NumberFormat(LOCALES[lang] ?? 'ru-RU', { maximumFractionDigits: 2 }).format(Math.round((v || 0) * 100) / 100);
  return n + ' ' + (currency === 'KZT' ? '₸' : currency);
}

/** Сумма поля в своей валюте по набору сделок. */
export function sumIn(rows: { totals: CurrencyTotals[] }[], currency: string, field: Field): number {
  return rows.reduce((s, r) => s + (r.totals.find(t => t.currency === currency)?.[field] ?? 0), 0);
}

/** Суммы в других валютах по набору сделок — «+ 3 000 USD · 200 CNY» или ''. */
export function othersText(rows: { totals: CurrencyTotals[] }[], currency: string, field: Field, lang = 'ru'): string {
  const map = new Map<string, number>();
  rows.forEach(r => r.totals.forEach(t => {
    if (t.currency !== currency && t[field] > 0) map.set(t.currency, (map.get(t.currency) ?? 0) + t[field]);
  }));
  return map.size ? '+ ' + [...map].map(([c, v]) => fmtMoney(v, c, lang)).join(' · ') : '';
}

/** Итоги нескольких сделок по валютам (долг и переплата — суммой по сделкам, без взаимозачёта между ними). */
export function mergeTotals(rows: { totals: CurrencyTotals[] }[]): CurrencyTotals[] {
  const map = new Map<string, CurrencyTotals>();
  rows.forEach(r => r.totals.forEach(t => {
    const m = map.get(t.currency) ?? { currency: t.currency, invoiced: 0, paid: 0, pending: 0, debt: 0, overpaid: 0 };
    (['invoiced', 'paid', 'pending', 'debt', 'overpaid'] as const).forEach(f => (m[f] += t[f]));
    map.set(t.currency, m);
  }));
  return [...map.values()];
}

/** Поле по одной сделке во всех валютах, своя первой: «2 500 EUR · 300 USD». */
export function totalsText(totals: CurrencyTotals[], currency: string, field: Field, lang = 'ru'): string {
  return [...totals]
    .filter(t => t[field] > 0)
    .sort((a, b) => (a.currency === currency ? -1 : b.currency === currency ? 1 : 0))
    .map(t => fmtMoney(t[field], t.currency, lang))
    .join(' · ');
}
